import React, { useEffect } from 'react'

import api from '@/services/api'
import createOgImage from '@/lib/createOgImage'

import { useGlobal } from '@/context/global'

import { Container } from '@/styles/pages/Instagram'
import Swiper, { SwiperSlide } from '@/components/Swiper'
import { OptimizedAsset } from '@/components/OptimizedAsset'
import Link from 'next/link'
import { GetStaticProps, InferGetStaticPropsType } from 'next'
import SEO from '@/components/SEO'

interface Posts {
  title: string
  image: string
}

interface InstagramStaticProps {
  posts: Posts[]
  ogImage: string
  links: {
    title: string
    image: string
    imageAvif?: string
    imageWebp?: string
    url: string
  }[]
}

type InstagramProps = InferGetStaticPropsType<
  GetStaticProps<InstagramStaticProps>
>

export default function Instagram({ ogImage, posts, links }: InstagramProps) {
  const { NavBar } = useGlobal()

  const swiperConfig = {
    spaceBetween: 20,
    slidesPerView: 'auto',
    mousewheel: true,
    navigation: true
  }

  useEffect(() => {
    NavBar.set(false)
    return () => NavBar.set(true)
  }, [])

  return (
    <Container>
      <SEO
        title="Posts do instagram"
        description="Postagens do instagram"
        image={ogImage}
      />

      <img
        src="https://res.cloudinary.com/solrachix/image/upload/v1643122128/WhatsApp_Image_2022-01-14_at_10.13.35_qiowkz.jpg"
        alt="Carlos Miguel"
      />

      <div className="content">
        <h1>Carlos Miguel</h1>
        <span>FULLSTACK DEVELOPER</span>
        <a className="email-cta" href="mailto:eu@carlos-miguel.dev">
          Enviar e-mail
        </a>
        <p>
          Sou desenvolvedor web há 7 anos. Sempre gostei de programar, design e
          animações e resolver soluções criativas e inovadoras. Minhas
          experiências na área de programação me fizeram evoluir como
          profissional, e como consequência do amor pelo que faço, desenvolver
          se tornou meu hobbie favorito.
        </p>

        <div className="links">
          <span>LINKS</span>

          <Swiper {...swiperConfig}>
            {links.map(link => (
              <SwiperSlide key={link.title}>
                <Link href={link.url}>
                  <div className="link">
                    {link.imageAvif || link.imageWebp ? (
                      <OptimizedAsset
                        source={link.image}
                        avif={link.imageAvif}
                        webp={link.imageWebp}
                        alt={link.title}
                      />
                    ) : (
                      <img src={link.image} alt={link.title} />
                    )}
                    <span>{link.title}</span>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        <div className="instagram-post-links">
          <span>INSTAGRAM POST LINKS</span>

          <Swiper {...swiperConfig}>
            {posts.map(post => (
              <SwiperSlide key={post.title}>
                <Link href={`/instagram/${post.title}`} target="_blank">
                  <div className="post">
                    <img src={post.image} />
                    <span>{post.title}</span>
                  </div>
                </Link>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </Container>
  )
}

export const getStaticProps: GetStaticProps<
  InstagramStaticProps
> = async () => {
  const ogImage = await createOgImage({
    title: `Instagram Posts
    `
  })

  try {
    const posts = await api.get<Posts[]>('/instagram-post')

    const links = [
      {
        title: 'Invencible Skyflight',
        image: '/images/mockups/invencible-skyflight.jpg',
        url: 'https://solrachix.github.io/invencible-skyflight'
      },
      {
        title: 'Portfolio Website',
        image: '/images/mockups/solrachix.home.png',
        url: '/'
      },
      {
        title: 'PacketShield',
        image: '/images/mockups/packetshield.png',
        imageAvif: '/images/mockups/packetshield.avif',
        imageWebp: '/images/mockups/packetshield.webp',
        url: 'https://packetshield.solrachix.com/'
      },
      {
        title: 'Papyrus SDK',
        image: '/images/mockups/papyrus-sdk.png',
        url: 'https://solrachix.github.io/Papyrus/'
      },
      {
        title: 'Gerenciador de ícones',
        image: '/images/mockups/icons-manager.png',
        url: 'https://icons-manager.solrachix.app/'
      }
    ]

    return {
      revalidate: 64 * 60 * 24, // um dia
      props: {
        posts: posts.data.reverse(),
        ogImage: ogImage,
        links
      }
    }
  } catch (error) {
    console.error('Failed to fetch instagram posts:', error)

    const links = [
      {
        title: 'Portfolio Website',
        image: '/images/mockups/solrachix.home.png',
        url: '/'
      },
      {
        title: 'Invencible Skyflight',
        image: '/images/mockups/invencible-skyflight.jpg',
        url: 'https://solrachix.github.io/invencible-skyflight'
      },
      {
        title: 'PacketShield',
        image: '/images/mockups/packetshield.png',
        imageAvif: '/images/mockups/packetshield.avif',
        imageWebp: '/images/mockups/packetshield.webp',
        url: 'https://packetshield.solrachix.com/'
      },
      {
        title: 'Papyrus SDK',
        image: '/images/mockups/papyrus-sdk.png',
        url: 'https://solrachix.github.io/Papyrus/'
      },
      {
        title: 'Gerenciador de ícones',
        image: '/images/mockups/icons-manager.png',
        url: 'https://icons-manager.solrachix.app/'
      }
    ]

    return {
      revalidate: 64 * 60 * 24,
      props: {
        posts: [],
        ogImage: ogImage,
        links
      }
    }
  }
}
